import { useEffect, useState } from "react";
import api from "@/utils/axios-utils";
import { isLessonVisible } from "@/constants/lessons";
import { Classroom, Lesson } from "@/types/tutor";
import LessonCard from "./LessonCard";
import TeacherWelcome from "./TeacherWelcome";
import { useAuth } from "@/hooks/useAuth";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import FilteredLessonsView from "@/components/FilteredLessonsView";
import FilterOptions from "@/components/FilterOptions";
import { useMyTrial } from "@/hooks/useMyTrial";
import { isTrialLocked } from "@/components/TrialBanner";
import { useAccounts } from "@/hooks/useAccounts";
import { useAuthStore } from "@/store/useAuthStore";
import { ChevronLeft, ChevronRight } from "lucide-react";

const LESSONS_PER_PAGE = 10;

// First name only, for the "[Name]'s library" header.
const firstNameOf = (full?: string | null): string =>
  (full || "").trim().split(/\s+/)[0] || "";

type SlideInfo = {
  index: number;
  name: string;
  slide_type?: string | null;
};

interface LessonWithSlides extends Lesson {
  slide_data: SlideInfo[];
  slide_count: number;
}

const PaginationControls = ({
  page,
  pageCount,
  onPrev,
  onNext,
  onPage,
  total,
}: {
  page: number;
  pageCount: number;
  onPrev: () => void;
  onNext: () => void;
  onPage: (page: number) => void;
  total: number;
}) =>
  total > 0 ? (
    <div className="et-pagination">
      <output>
        {(page - 1) * LESSONS_PER_PAGE + 1}–
        {Math.min(page * LESSONS_PER_PAGE, total)} of {total} lessons
      </output>
      <nav aria-label="Lesson pages">
        <button
          type="button"
          onClick={onPrev}
          disabled={page <= 1}
          className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 py-1.5 text-sm font-semibold text-[#1F2A44] transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronLeft size={16} />
          <span className="sr-only">Previous</span>
        </button>
        {Array.from({ length: Math.min(5, pageCount) }, (_, i) => {
          const target = Math.max(1, Math.min(page - 2, pageCount - 4)) + i;
          return (
            <button
              key={target}
              type="button"
              aria-label={`Page ${target}`}
              aria-current={target === page ? "page" : undefined}
              onClick={() => onPage(target)}
            >
              {target}
            </button>
          );
        })}
        <button
          type="button"
          onClick={onNext}
          disabled={page >= pageCount}
          className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 py-1.5 text-sm font-semibold text-[#1F2A44] transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <span className="sr-only">Next</span>
          <ChevronRight size={16} />
        </button>
      </nav>
    </div>
  ) : null;

const TeacherLessonsView = () => {
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [lessons, setLessons] = useState<LessonWithSlides[] | null>(null);
  const [loadingLessons, setLoadingLessons] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedLessonId, setExpandedLessonId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);

  const safePage = Math.min(page, pageCount);
  const onPrev = () => setPage((p) => Math.max(1, p - 1));
  const onNext = () => setPage((p) => Math.min(pageCount, p + 1));

  const { isLoading, user } = useAuth();
  const { trial } = useMyTrial();
  const locked = trial ? isTrialLocked(trial) : false;

  const { accounts } = useAccounts();
  const actingTeacherId = useAuthStore((s) => s.actingTeacherId);
  const viewingAs =
    actingTeacherId !== null
      ? accounts.find((a) => a.teacher_id === actingTeacherId && !a.is_you)
      : undefined;
  // Whose library this is: the view-as target, else the logged-in user.
  const libraryOwnerName = viewingAs
    ? firstNameOf(viewingAs.name || viewingAs.email)
    : firstNameOf(user?.name);

  const { setCurrentLessonId } = useSlideStore();

  useEffect(() => {
    if (!isLoading && user?.id) {
      fetchLessons();
      fetchClassrooms();
      setCurrentLessonId(null);
    }
  }, [isLoading, user]);

  const fetchClassrooms = async () => {
    const response = await api.get("/classrooms");
    setClassrooms(response.data);
  };

  const fetchLessons = async () => {
    if (!user?.id) {
      setError("User not authenticated");
      setLoadingLessons(false);
      return;
    }

    try {
      setError(null);
      setPage(1);
      const response = await api.get(
        `/lessons/?include_slides=true&user_id=${user.id}`,
      );
      setLessons(
        response.data.filter((l: LessonWithSlides) => isLessonVisible(l.id)),
      );
    } catch (err) {
      console.error("Error fetching lessons:", err);
      setError("Failed to load lessons. Please try again.");
    } finally {
      setLoadingLessons(false);
    }
  };

  const handleLessonToggle = (lessonId: number) => {
    setExpandedLessonId(expandedLessonId === lessonId ? null : lessonId);
  };

  if (error) {
    return (
      <div role="alert" className="et-group-error">
        <div className="text-center text-lg text-[#687368]">{error}</div>
        <button onClick={fetchLessons} className="et-primary">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="et-teacher-library">
      <TeacherWelcome />
      <div
        className={`flex flex-col${locked ? " pointer-events-none select-none opacity-60 blur-[2px]" : ""}`}
      >
        <div className="flex flex-col gap-4">
          <div className="et-section-heading">
            <h2>
              {libraryOwnerName
                ? `${libraryOwnerName}’s library`
                : "Your library"}{" "}
              <span className="et-count">{lessons?.length ?? 0}</span>
            </h2>
            <img src="/design/library.webp" alt="" />
          </div>
          <div className="et-library">
            <FilteredLessonsView
              lessons={lessons ?? []}
              loading={isLoading || loadingLessons}
              renderFilters={(onFiltersChange) => (
                <FilterOptions
                  onFiltersChange={(update) => {
                    setPage(1);
                    onFiltersChange(update);
                  }}
                  excludeOptions={["mastery"]}
                />
              )}
              emptyState={
                <div className="text-center py-12">
                  <div className="bg-white rounded-lg shadow-md p-8">
                    <div className="text-gray-400 mb-4">
                      <svg
                        className="mx-auto h-12 w-12"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No lessons available
                    </h3>
                    <p className="text-gray-500">
                      Create your first lesson to start building your
                      curriculum.
                    </p>
                  </div>
                </div>
              }
            >
              {(filteredLessons) => {
                const newPageCount = Math.max(
                  1,
                  Math.ceil(filteredLessons.length / LESSONS_PER_PAGE),
                );
                if (newPageCount !== pageCount) {
                  setTimeout(() => setPageCount(newPageCount), 0);
                }
                const start = (safePage - 1) * LESSONS_PER_PAGE;
                const pageLessons = filteredLessons.slice(
                  start,
                  start + LESSONS_PER_PAGE,
                );
                return (
                  <div className="w-full">
                    <div className="et-catalog-heading" aria-hidden="true">
                      <span>LESSON</span>
                      <span>LAST UPDATED</span>
                      <span />
                    </div>
                    {pageLessons.map((lesson) => (
                      <LessonCard
                        key={lesson.id}
                        lesson={lesson}
                        classrooms={classrooms}
                        isExpanded={expandedLessonId === lesson.id}
                        onToggle={() => handleLessonToggle(lesson.id)}
                      />
                    ))}
                    <PaginationControls
                      page={safePage}
                      pageCount={pageCount}
                      onPrev={onPrev}
                      onNext={onNext}
                      onPage={setPage}
                      total={filteredLessons.length}
                    />
                  </div>
                );
              }}
            </FilteredLessonsView>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherLessonsView;
